import React from 'react';
import { motion } from 'framer-motion';
import {
    Briefcase, CheckCircle, XCircle, Trash2,
    Eye, Clock, MapPin, Building2, Calendar,
    Filter, Search, Share2
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Card } from '../../../components/ui/card';
import { toast } from 'react-toastify';

const JobsSection = ({
    adminJobs,
    adminJobFilter,
    setAdminJobFilter,
    searchQuery,
    setSelectedJobForDetails,
    handleJobApproval,
    setJobToReject,
    setShowRejectionModal,
    deleteJob,
    isMobile
}) => {
    const filteredJobs = adminJobs.filter(j => {
        const matchesFilter = adminJobFilter === 'all' || j.status === adminJobFilter;
        const matchesSearch = !searchQuery ||
            j.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            j.companyName?.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesFilter && matchesSearch;
    });

    const stats = {
        all: adminJobs.length,
        pending: adminJobs.filter(j => j.status === 'pending').length,
        approved: adminJobs.filter(j => j.status === 'approved').length,
        rejected: adminJobs.filter(j => j.status === 'rejected').length,
    };

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div>
                    <h3 className="text-2xl font-black text-foreground tracking-tight flex items-center gap-2">
                        <Briefcase className="text-primary" /> Job Management
                    </h3>
                    <p className="text-muted-foreground text-xs font-bold uppercase tracking-widest mt-1 opacity-70">
                        Review and moderate job postings from companies
                    </p>
                </div>

                <div className="flex bg-secondary/20 p-1 rounded-2xl border border-border/40 backdrop-blur-md self-stretch md:self-auto overflow-x-auto no-scrollbar">
                    {['all', 'pending', 'approved', 'rejected'].map(f => (
                        <button
                            key={f}
                            onClick={() => setAdminJobFilter(f)}
                            className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all whitespace-nowrap flex items-center gap-2 ${adminJobFilter === f
                                ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                                : 'text-muted-foreground hover:text-foreground hover:bg-secondary/40'
                                }`}
                        >
                            {f} <span className="opacity-60 text-[10px]">({stats[f]})</span>
                        </button>
                    ))}
                </div>
            </div>

            {filteredJobs.length === 0 ? (
                <Card className="p-20 text-center bg-card/40 backdrop-blur-xl border-dashed border-2 border-border/60 rounded-[2.5rem]">
                    <Briefcase size={48} className="mx-auto mb-4 text-muted-foreground/30" />
                    <div className="text-lg font-black text-foreground tracking-tight mb-2">No {adminJobFilter} jobs found</div>
                    <p className="text-muted-foreground text-sm font-bold max-w-xs mx-auto opacity-70">
                        {searchQuery
                            ? `We couldn't find any jobs matching "${searchQuery}" in the ${adminJobFilter} category.`
                            : `There are currently no job postings in the ${adminJobFilter} status.`}
                    </p>
                </Card>
            ) : (
                <div className="grid grid-cols-1 gap-4">
                    {filteredJobs.map((job, i) => (
                        <motion.div
                            key={job.id}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.05 }}
                            className="group"
                        >
                            <Card className="p-6 bg-card/40 backdrop-blur-xl border-border/40 rounded-[2rem] hover:border-primary/20 hover:shadow-2xl transition-all duration-300 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 blur-3xl rounded-full -mr-16 -mt-16 group-hover:bg-primary/10 transition-colors" />

                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 relative z-10">
                                    <div className="flex-1 space-y-4">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 font-black text-[10px] py-0.5 rounded-lg uppercase tracking-widest">
                                                {job.type || 'Job'}
                                            </Badge>
                                            {job.workMode && (
                                                <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/20 font-bold text-[10px] py-0.5 rounded-lg">
                                                    {job.workMode}
                                                </Badge>
                                            )}
                                            {job.category && (
                                                <Badge variant="outline" className="bg-secondary/40 text-muted-foreground border-border/60 font-medium text-[10px] py-0.5 rounded-lg">
                                                    {job.category}
                                                </Badge>
                                            )}
                                        </div>

                                        <div>
                                            <h4 className="text-lg font-black text-foreground tracking-tight mb-1 group-hover:text-primary transition-colors">{job.title}</h4>
                                            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm font-bold text-muted-foreground opacity-80">
                                                <span className="flex items-center gap-1.5"><Building2 size={14} className="text-primary/60" /> {job.companyName}</span>
                                                <span className="flex items-center gap-1.5"><MapPin size={14} className="text-primary/60" /> {job.location}</span>
                                                {job.stipend && <span className="flex items-center gap-1.5"><Clock size={14} className="text-primary/60" /> {job.stipend}</span>}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">
                                            <Calendar size={12} />
                                            Posted {job.createdAt?.toDate ? job.createdAt.toDate().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Recently'}
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-3 md:self-center">
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => setSelectedJobForDetails(job)}
                                            className="w-11 h-11 rounded-2xl border-border/60 bg-background/40 hover:bg-primary/10 hover:text-primary transition-all"
                                            title="View Details"
                                        >
                                            <Eye size={18} />
                                        </Button>

                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => {
                                                const url = `${window.location.origin}/job-portal/opportunities?job=${job.id}`;
                                                if (navigator.share) {
                                                    navigator.share({ title: `${job.title} at ${job.companyName}`, text: `Check out this opportunity: ${job.title} at ${job.companyName}`, url }).catch(() => { });
                                                } else {
                                                    navigator.clipboard.writeText(url).then(() => toast.success('Share link copied!'));
                                                }
                                            }}
                                            className="w-11 h-11 rounded-2xl border-border/60 bg-background/40 hover:bg-primary/10 hover:text-primary transition-all"
                                            title="Share Job"
                                        >
                                            <Share2 size={18} />
                                        </Button>

                                        {adminJobFilter === 'pending' && (
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    onClick={() => handleJobApproval(job, 'approved')}
                                                    className="bg-emerald-500 hover:bg-emerald-600 text-white font-black text-xs uppercase tracking-widest rounded-2xl px-6 py-5 shadow-lg shadow-emerald-500/20 flex items-center gap-2 h-auto"
                                                >
                                                    <CheckCircle size={16} /> Approve
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    onClick={() => { setJobToReject(job); setShowRejectionModal(true); }}
                                                    className="border-rose-500/40 text-rose-500 hover:bg-rose-500 hover:text-white font-black text-xs uppercase tracking-widest rounded-2xl px-6 py-5 h-auto transition-all"
                                                >
                                                    <XCircle size={16} /> Reject
                                                </Button>
                                            </div>
                                        )}

                                        {adminJobFilter !== 'pending' && (
                                            <Badge className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest ${job.status === 'approved'
                                                ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
                                                : 'bg-rose-500/10 text-rose-500 border-rose-500/20'
                                                }`}>
                                                {job.status}
                                            </Badge>
                                        )}

                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => deleteJob(job.id)}
                                            className="w-11 h-11 rounded-2xl bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white transition-all ml-auto md:ml-0"
                                            title="Delete Permanently"
                                        >
                                            <Trash2 size={18} />
                                        </Button>
                                    </div>
                                </div>
                            </Card>
                        </motion.div>
                    ))}
                </div>
            )}
        </motion.div>
    );
};

export default JobsSection;
